/*
  Shopify defaults <variant-selects> to whichever option value was created
  first for the product, which is normally the smallest size. On Tracker that
  is X-Small, and two of the five colours (Steel Grey, Hot Pink) are only sold
  from Small upward, so the very first thing a customer sees is two dead
  swatches: fully opaque, fully tappable, and inert. Reordering the underlying
  variants to fix this is not reliable through the Admin API, so this handles
  it at the point of display instead.

  Opt-in and data-driven. Does nothing unless a product sets the
  custom.default_option_override metafield to an option value that genuinely
  exists and is enabled, so this can never disable or misrepresent a real
  product. Never overrides a ?variant= link that resolved, so a shared link,
  a back button, or a bookmark always lands on the exact variant it named.

  Runs once per page load, before the customer has touched anything. It picks
  the option value, then does nothing more: fires the same native change event
  a real click would, so every downstream system (price, URL, cart form,
  cross-filtering, the kit picker's own variant sync) updates exactly as it
  already does for a real selection. Nothing here duplicates or second-guesses
  those systems.
*/
(() => {
  const requested = new URLSearchParams(location.search).get('variant');
  // A ?variant= Shopify could not resolve renders the first variant, the same as no parameter, and the kit's server prices already assume the override then.
  function variantResolved() {
    if (!requested) return false;
    const idInput = document.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
    return !idInput || idInput.value === requested;
  }

  function applyOverride(root) {
    const override = (root.dataset.defaultOptionOverride || '').trim();
    if (!override) return;

    // <variant-selects> is a custom element. Its connectedCallback (which
    // attaches the 'change' listener everything downstream depends on, price,
    // the cart form's id input, the kit picker's own variant sync) can still
    // be pending this early, even after DOMContentLoaded: browsers upgrade
    // custom elements already in the parsed HTML as a queued microtask, not
    // synchronously. A synthetic click before that upgrade completes toggles
    // the native radio (the browser does that on its own) but nothing else
    // updates, since there is no listener yet to hear it. Confirmed by
    // testing: the exact same click, fired later by hand, updates the price
    // and the cart form correctly every time. customElements.upgrade() forces
    // that upgrade to happen now rather than hoping a delay is long enough.
    customElements.upgrade(root);

    const inputs = root.querySelectorAll('input[type="radio"], select option');
    const match = [...inputs].find((el) => {
      const value = el.value !== undefined ? el.value : el.getAttribute('value');
      return (value || '').trim().toLowerCase() === override.toLowerCase();
    });
    if (!match) return;

    if (match.tagName === 'INPUT') {
      if (match.disabled || match.checked) return;
      const label = match.closest('label') || document.querySelector(`label[for="${match.id}"]`);
      (label || match).click();
    } else if (match.tagName === 'OPTION') {
      if (match.disabled || match.selected) return;
      const select = match.closest('select');
      if (!select) return;
      select.value = match.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function init() {
    if (variantResolved()) return;
    const roots = document.querySelectorAll('variant-selects[data-default-option-override]');
    if (!roots.length) return;
    // These are lazy loaded and are what move the cart variant and price; clicking before they exist only flips the radio.
    const needed = ['product-info', 'product-form', 'kit-picker'].filter((tag) => document.querySelector(tag));
    Promise.all(needed.map((tag) => customElements.whenDefined(tag))).then(() => roots.forEach(applyOverride));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
